// SSPICE Shared Ordering Engine — framework-agnostic cart logic.
// Single source of truth for cart math, used by BOTH the customer storefront and the POS.
// Keep this PURE (no React, no network) so it stays reusable and testable.

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// A stable signature so identical item+variant+modifiers merge into one line.
export function lineSignature({ menu_item_id, variant, modifiers }) {
  const variantKey = variant?.id || variant?.name || '';
  const modKey = (modifiers || [])
    .map((m) => m.id || m.name)
    .sort()
    .join(',');
  return `${menu_item_id}::${variantKey}::${modKey}`;
}

// Per-unit price = base (or variant) price + sum of modifier prices.
export function unitPrice(item) {
  const base = item.variant?.price != null ? item.variant.price : item.price;
  const mods = (item.modifiers || []).reduce((s, m) => s + (m.price || 0), 0);
  return round2(base + mods);
}

export function lineTotal(item) {
  return round2(unitPrice(item) * (item.qty || 0));
}

export function createCartItem(menuItem, { qty = 1, variant = null, modifiers = [], notes = '' } = {}) {
  const base = {
    menu_item_id: menuItem.id,
    menu_item_name: menuItem.name,
    price: menuItem.price,
    image_url: menuItem.image_url || menuItem.images?.image || null,
    variant,
    modifiers,
    notes,
    qty,
  };
  return { ...base, signature: lineSignature(base) };
}

export function addItem(cart, cartItem) {
  const idx = cart.findIndex((c) => c.signature === cartItem.signature);
  if (idx >= 0) {
    const next = [...cart];
    next[idx] = { ...next[idx], qty: next[idx].qty + cartItem.qty };
    return next;
  }
  return [...cart, cartItem];
}

export function updateQty(cart, signature, qty) {
  if (qty <= 0) return cart.filter((c) => c.signature !== signature);
  return cart.map((c) => (c.signature === signature ? { ...c, qty } : c));
}

export function removeItem(cart, signature) {
  return cart.filter((c) => c.signature !== signature);
}

export function itemCount(cart) {
  return cart.reduce((s, c) => s + (c.qty || 0), 0);
}

// Central totals calculator. Discounts/taxes are passed in so the same engine
// serves customer checkout and POS (which layers overrides/BOGO on top).
export function computeTotals(cart, { taxPercent = 0, discount = 0, deliveryFee = 0 } = {}) {
  const subtotal = round2(cart.reduce((s, c) => s + lineTotal(c), 0));
  const discountAmt = round2(Math.min(discount, subtotal));
  const taxable = round2(subtotal - discountAmt);
  const tax = round2((taxable * taxPercent) / 100);
  const total = round2(taxable + tax + deliveryFee);
  return { subtotal, discount: discountAmt, tax, taxPercent, deliveryFee: round2(deliveryFee), total, count: itemCount(cart) };
}
